/**
 * LLM-Based Ticker Extraction Service
 * Uses AI to intelligently extract stock tickers from natural language
 *
 * Features:
 * - Context-aware extraction (distinguishes "Apple stock" from "apple pie")
 * - Multi-ticker support (for group chats and comparisons)
 * - High confidence scoring
 * - Fast response with structured output
 */

import { perplexityService } from '../api/perplexity.service';

export interface LLMTickerExtractionResult {
  tickers: string[]; // Array of ticker symbols (can be empty)
  primary: string | null; // Primary ticker if single focus
  confidence: number; // 0-1 score
  context: string; // Brief explanation of what was detected
}

class LLMTickerExtractor {
  /**
   * Extract stock tickers from natural language query using LLM
   *
   * @param query - User's natural language query
   * @returns Extraction result with tickers and confidence
   */
  async extractTickers(query: string): Promise<LLMTickerExtractionResult> {
    try {
      // Construct prompt for ticker extraction
      const prompt = this.buildExtractionPrompt(query);

      // Use Perplexity AI for extraction (fast, structured)
      const result = await perplexityService.askQuestion(prompt);

      if (result.error || !result.data) {
        console.error('LLM ticker extraction failed:', result.error);
        return this.emptyResult();
      }

      // Parse LLM response
      return this.parseLLMResponse(result.data.content);
    } catch (error) {
      console.error('LLM ticker extraction error:', error);
      return this.emptyResult();
    }
  }

  /**
   * Build structured prompt for ticker extraction
   */
  private buildExtractionPrompt(query: string): string {
    return `You are a stock ticker extraction assistant. Extract stock ticker symbols from the user's query.

Rules:
1. Only extract VALID stock ticker symbols (e.g., TSLA, AAPL, MSFT)
2. Understand context - distinguish between stocks and other meanings (e.g., "Apple stock" vs "apple pie")
3. If multiple tickers mentioned, extract all of them
4. If no stock is mentioned, return empty array
5. Return confidence score (0-1) based on clarity

Examples:
Query: "How is Tesla doing?"
Response: {"tickers": ["TSLA"], "primary": "TSLA", "confidence": 0.95, "context": "Single stock query about Tesla"}

Query: "Compare Apple and Microsoft"
Response: {"tickers": ["AAPL", "MSFT"], "primary": "AAPL", "confidence": 0.9, "context": "Comparison between two stocks"}

Query: "I like apple pie"
Response: {"tickers": [], "primary": null, "confidence": 0.95, "context": "Not about stocks, refers to food"}

Query: "tell me about tmdx"
Response: {"tickers": ["TMDX"], "primary": "TMDX", "confidence": 0.85, "context": "Query about TransMedics Group stock"}

Query: "What's the market doing today?"
Response: {"tickers": [], "primary": null, "confidence": 0.9, "context": "General market query, no specific stock"}

Now extract from this query:
Query: "${query}"

Respond ONLY with valid JSON in the format shown above. No additional text.`;
  }

  /**
   * Parse LLM response into structured result
   */
  private parseLLMResponse(content: string): LLMTickerExtractionResult {
    try {
      // Extract JSON from response (LLM might add extra text)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.warn('No JSON found in LLM response:', content);
        return this.emptyResult();
      }

      const parsed = JSON.parse(jsonMatch[0]);

      // Validate response structure
      if (!Array.isArray(parsed.tickers)) {
        console.warn('Invalid tickers format in LLM response');
        return this.emptyResult();
      }

      return {
        tickers: parsed.tickers.map((t: string) => t.toUpperCase()),
        primary: parsed.primary ? parsed.primary.toUpperCase() : null,
        confidence: parsed.confidence || 0.5,
        context: parsed.context || 'No context provided',
      };
    } catch (error) {
      console.error('Failed to parse LLM response:', error);
      return this.emptyResult();
    }
  }

  /**
   * Return empty result for failed extractions
   */
  private emptyResult(): LLMTickerExtractionResult {
    return {
      tickers: [],
      primary: null,
      confidence: 0,
      context: 'Extraction failed',
    };
  }

  /**
   * Extract single ticker (for backward compatibility)
   * Returns the primary ticker or first ticker in list
   */
  async extractSingleTicker(query: string): Promise<{
    ticker: string | null;
    confidence: number;
    context: string;
  }> {
    const result = await this.extractTickers(query);

    return {
      ticker: result.primary || (result.tickers.length > 0 ? result.tickers[0] : null),
      confidence: result.confidence,
      context: result.context,
    };
  }
}

// Export singleton instance
export const llmTickerExtractor = new LLMTickerExtractor();
